import React, { useRef, useEffect } from "react";
import "../InvoiceModal.css";
import PrintableInvoice from "./PrintableInvoice";
import html2pdf from "html2pdf.js";
import { FaRegTimesCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { MdArrowCircleDown } from "react-icons/md";
import { BsArrowRepeat } from "react-icons/bs";
import International from "./InvoiceSubComponents/International";
import ShipmentSummary from "./InvoiceSubComponents/ShipmentSummary";
import Tracking from "./InvoiceSubComponents/Tracking";
import Header from "./InvoiceSubComponents/Header";
import ShipmentDetails from "./InvoiceSubComponents/ShipmentDetails";
import Footer from "./InvoiceSubComponents/Footer";
import BillingUser from "./InvoiceSubComponents/BillingUser";
import Notes from "./InvoiceSubComponents/NotesUser";
import InvoiceActionsDropdown from "./InvoiceSubComponents/InvoiceActionsWithDocs";
import { showSnackBar } from "../../../store/common/snackBarSlice";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

const FullPageInvoice = ({ data, SetStepCheck, Reset }) => {
  const invoiceRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation("finances");

  function handleClick() {
    navigate("/shipments");
  }

  const downloadDocs = async (data, type) => {
    const DOWNLOAD_CONFIG = {
      label: {
        fileKey: "label",
        label: t("invoice.buttons.label"),
      },
      declaration: {
        fileKey: "invoice",
        label: t("invoice.buttons.declaration"),
      },
    };

    const config = DOWNLOAD_CONFIG[type];

    if (!config) {
      dispatch(
        showSnackBar({
          text: t("invoice.invalidDownloadType"),
          severity: "error",
        }),
      );
      return;
    }

    const { fileKey, label } = config;
    // const loadingKey = `${type}${data?.id}`;

    // setLabelDownloading(loadingKey); // no loader available currently

    try {
      const fileUrl = data?.[fileKey]?.file;

      if (!fileUrl) {
        throw new Error("File URL missing");
      }

      const response = await fetch(fileUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/pdf",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed with status ${response.status}`);
      }

      const blob = await response.blob();

      if (!blob || blob.size === 0) {
        throw new Error("Empty file");
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `${data?.tattension} ${data?.shipment_tracking_id} ${label}.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`[${label} Download Error]`, error);

      dispatch(
        showSnackBar({
          text: t("common:messages.somethingWentWrong"),
          severity: "error",
        }),
      );
    } finally {
      // setLabelDownloading(""); // no loader available currently
    }
  };

  // i18n.t, not t: the mount effect below calls this function, and using t
  // here would make the function one of that effect's dependencies.
  const downloadPdf = async (data) => {
    const input = invoiceRef.current;
    const options = {
      filename: `${
        data.tattension + " " + data.shipment_tracking_id
      } ${i18n.t("finances:invoice.buttons.invoice")}.pdf`,
      format: "A4",
      jsPDF: { unit: "px", format: [830, 1000], orientation: "p" },
      image: { type: "jpeg", quality: 0.98 },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      html2canvas: { scale: 2, useCORS: true },
    };

    await html2pdf().set(options).from(input).save();
  };

  useEffect(() => {
    if (data) {
      downloadPdf(data);
      downloadDocs(data, "label");
      if (data?.invoice) {
        downloadDocs(data, "declaration");
      }
    }
  }, []);

  return (
    <>
      <div
        style={{
          zIndex: "-1000",
          position: "absolute",
          visibility: "hidden",
          left: "-1000",
        }}
      >
        <div className="fpinvoice__modal_wrap shipment__fp_invoice_print_wrap">
          <PrintableInvoice invoiceRef={invoiceRef} data={data} />
        </div>
      </div>

      <div className="fpinvoice__modal_wrap shipment__fp_invoice_custom_wrap">
        <div className="fpinvoice__modal_box shipment__fp_invoice_custom_box">
          <div className="invoice__modal_downloadinvoicewrap">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {/* Download Invoice */}
              <button
                onClick={() => downloadPdf(data)}
                className="flex items-center gap-1.5 rounded-md bg-brand px-2.5 py-1.5 text-xs font-medium text-white shadow hover:bg-brand-hover
                  sm:gap-2 sm:px-3 sm:py-2 sm:text-sm sm:leading-none"
              >
                <MdArrowCircleDown className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>{t("invoice.buttons.invoice")}</span>
              </button>

              {/* Download Label */}
              <button
                onClick={() => downloadDocs(data, "label")}
                className="flex items-center gap-1.5 rounded-md border border-ui-border bg-ui-surface px-2.5 py-1.5 text-xs font-medium text-ui-text hover:bg-ui-surface-sunken sm:gap-2 sm:px-3 sm:py-2 sm:text-sm sm:leading-none"
              >
                <MdArrowCircleDown className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>{t("invoice.buttons.label")}</span>
              </button>

              {/* Manage Declaration */}
              {data?.invoice && (
                <>
                  <InvoiceActionsDropdown
                    documents={data?.shipping_document || []}
                    shipmentId={data?.id}
                    onDownloadDeclaration={() =>
                      downloadDocs(data, "declaration")
                    }
                    onDownloadAllZip={() => {}}
                    onDocumentsUpdate={(documents) => {
                      SetStepCheck((prev) => ({
                        ...prev,
                        FinalData: {
                          ...prev.FinalData,
                          shipping_document: documents,
                        },
                      }));
                    }}
                    onDocumentDelete={(deleteId) => {
                      SetStepCheck((prev) => ({
                        ...prev,
                        FinalData: {
                          ...prev.FinalData,
                          shipping_document:
                            prev.FinalData.shipping_document.filter(
                              (d) => d.id !== deleteId,
                            ),
                        },
                      }));
                    }}
                  />
                </>
              )}

              {/* Reset / Clear Data */}
              <button
                onClick={Reset}
                className="flex items-center gap-1.5 rounded-md bg-brand px-2.5 py-1.5 text-xs font-medium text-white shadow hover:bg-brand-hover
                  sm:gap-2 sm:px-3 sm:py-2 sm:text-sm sm:leading-none"
              >
                <BsArrowRepeat className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>{t("invoice.buttons.shipMore")}</span>
              </button>

              {/* Close */}
              <button
                onClick={handleClick}
                className="flex items-center gap-1.5 rounded-md bg-state-danger px-2.5 py-1.5 text-xs font-medium text-white shadow
                  hover:bg-state-danger sm:gap-2 sm:px-3 sm:py-2 sm:text-sm sm:leading-none"
              >
                <FaRegTimesCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden sm:inline">
                  {t("common:actions.close")}
                </span>
              </button>
            </div>
          </div>

          <Header data={data} />
          <ShipmentSummary data={data} />
          <International data={data} />
          <ShipmentDetails data={data} />
          <BillingUser data={data} />
          <Notes data={data} />
          <Tracking data={data} />
          <Footer data={data} />
        </div>
      </div>
    </>
  );
};

export default FullPageInvoice;
